import "./ChatpracticeEvaluation.scss";
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Typography,
} from "@mui/material";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import IconButtonField from "../Button/IconButtonField";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import TextInputFieldC from "../TextInput/TextInputFieldC";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import Markdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import "katex/dist/katex.min.css";
import { motion } from "framer-motion";
import TypewriterMarkdown from "../TypewriterMarkdown/TypewriterMarkdown";

import {
  ChatpracticeEvaluation as chatpracticeEvaluationThunk,
  clearChatpracticeEvaluationStatus,
} from "../../Redux/Slices/ChatbotpracticeEvaluation/ChatpracticeEvaluationSlicer";
import { ChatpracticeEvaluationProps, IChatpracticeEvaluation } from "../ChatbotPracticeEvaluation/IChatpracticeEvaluation";
import AvatarImage from "../../Assets/Images/Chatbot_Avatar.png";
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import CheckIcon from "@mui/icons-material/Check"; // Optional for a tick
import sendImage from "../../Assets/Images/Send_icon.svg"


const drawerWidth = 450;

const ChatDotsLoading = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", height: 20, pl: 1 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#6b7280",
          animation: `dotBounce 1.2s infinite`,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ))}
    <style>
      {`
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}
    </style>
  </Box>
);

const ChatpracticeEvaluation: FunctionComponent<ChatpracticeEvaluationProps> = (props) => {
  const dispatch = useAppDispatch();

  // Use correct slice for practice evaluation chat!
  const { data: chatbotData, isLoading: chatloading } = useAppSelector(
    (state) => state.ChatpracticeEvaluationSlicer
  );
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  const { open, setOpen, student_test_id } = props;
  const divRef = useRef<HTMLDivElement>(null);

  const methods = useForm<IChatpracticeEvaluation>();
  const formRef = useRef<HTMLFormElement>(null);

  // Store full chat (user + bot messages)
  const [chatHistory, setChatHistory] = useState<
    { sender: "user" | "bot"; message: string }[]
  >([]);

  // Clean ChatGPT HTML tags and normalize spacing
  const cleanMessage = (text = "") => {
    return text
      .replace(/<p><br><\/p>/g, "\n")
      .replace(/<br\s*\/?>/g, "\n")
      .replace(/<\/p>/g, "\n\n")
      .replace(/<p>/g, "")
      .replace(/<[^>]+>/g, "");
  };

  const onSubmit: SubmitHandler<IChatpracticeEvaluation> = (data: any) => {
    if (chatloading || isTyping) return;

    const userMessage = data?.message;
    if (!userMessage?.trim()) return;

    // Format chat history for payload
    const chatContext = chatHistory
      .map(
        (chat) =>
          `${chat.sender === "user" ? "user" : "bot"}: ${cleanMessage(
            chat.message
          )}`
      )
      .join(", ");

    const payloadMessage = `Previous message:[ ${chatContext} ] , user asked message: ${userMessage}`;

    setChatHistory((prev) => [
      ...prev,
      { sender: "user", message: userMessage },
    ]);

    dispatch(
      chatpracticeEvaluationThunk({
        message: payloadMessage,
        student_test_id: props.student_test_id, // <-- Use prop here
      })
    );

    methods.reset({ message: "" });
  };

  useEffect(() => {
    if (
      chatbotData &&
      (typeof chatbotData === "string" || chatbotData?.message?.trim())
    ) {
      const botMessage =
        typeof chatbotData === "string"
          ? chatbotData
          : chatbotData.message || "";

      setChatHistory((prev) => [
        ...prev,
        { sender: "bot", message: botMessage },
      ]);
      setIsTyping(true);
    }
  }, [chatbotData]);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.scrollTop = divRef.current.scrollHeight;
    }
  }, [chatHistory]);

  return (
    <>
      <Drawer
        sx={{
          width: drawerWidth,
          marginTop: "60px",
          "& .MuiDrawer-paper": {
            padding: 1,
            width: drawerWidth,
            display: "flex",
            flexDirection: "column",
            zIndex: 100000,
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <header className="chat_bot_headerss" style={{ display: "flex", alignItems: "center", padding: "12px 16px", gap: "12px" }}>
          <span style={{
            borderRadius: "50%",
            background: "linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)",
            width: 40,
            height: 40,
            justifyContent: "center",
            padding: 4,
            fontSize: "14px",

          }}><Avatar
              src={AvatarImage}
              className="chatbot-avatar"
              alt="Profile"
              sx={{ width: 32, height: 32 }}
            /></span>
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "18px", lineHeight: 1.1 }}>
              Elliot
            </Typography>
            <Typography sx={{ fontWeight: 400, fontSize: "12px", color: "#6b7280", lineHeight: 1.1 }}>
              AI Study Assistant
            </Typography>
          </Box>
          <IconButtonField
            sx={{ ml: "auto" }}
            onClick={() => {
              setOpen(false);
              dispatch(clearChatpracticeEvaluationStatus());
            }}
          >
            <CloseIcon />
          </IconButtonField>
        </header>

        <Divider />

        <div
          ref={divRef}
          className="message_area"
          style={{ flexGrow: 1, overflowY: "auto" }}
        >
          <Box sx={{ p: 2 }}>
            {/* Intro message */}
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                mb: 1,
                display: "flex",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <span style={{
                borderRadius: "50%",
                background: "linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)",
                width: 30,
                height: 30,
                justifyContent: "center",
                padding: 4
              }}>
                <Avatar
                  src={AvatarImage}
                  className="chatbot-avatar"
                  alt="Profile"
                  sx={{ width: 22, height: 22 }}
                />
              </span>
              Elliot
            </Typography>
            <Box
              sx={{
                backgroundColor: "#f3f4f6",
                borderRadius: "12px",
                p: 2,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.5, fontSize: "14px" }}
              >
                Hi! I'm Elliot, your AI study assistant. I'm here to help you with your studies and answer any questions you might have about your preparation!
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 2, mb: 2 }}>
            {/* Feedback buttons */}
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}>
              <IconButtonField
                size="small"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: feedback === "helpful" ? "#4caf50" : "#fff",
                  color: feedback === "helpful" ? "#fff" : "#333",
                  borderRadius: "8px",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  boxShadow: feedback === "helpful" ? "0 2px 8px rgba(76,175,80,0.12)" : "none",
                  transition: "background 0.2s, color 0.2s",
                  "&:hover": {
                    backgroundColor: "#4caf50",
                    color: "#fff",
                  },
                }}
                onClick={() => setFeedback("helpful")}
              >
                <ThumbUpOffAltIcon fontSize="small" />
                Helpful
                {feedback === "helpful" && <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />}
              </IconButtonField>

              <IconButtonField
                size="small"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: feedback === "not_helpful" ? "#f44336" : "#fff",
                  color: feedback === "not_helpful" ? "#fff" : "#333",
                  borderRadius: "8px",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  boxShadow: feedback === "not_helpful" ? "0 2px 8px rgba(76,175,80,0.12)" : "none",
                  transition: "background 0.2s, color 0.2s",
                  "&:hover": {
                    backgroundColor: "#f44336",
                    color: "#fff",
                  },
                }}
                onClick={() => setFeedback("not_helpful")}
              >
                <ThumbDownOffAltIcon fontSize="small" />
                Not helpful
                {feedback === "not_helpful" && <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />}
              </IconButtonField>
            </Box>
            {/* Suggestion buttons */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {["Study tips", "How can I improve?", "Practice recommendations", "General help"].map(
                (text, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "5px",
                      px: 2,
                      py: 0.5,
                      fontSize: "13px",
                      fontWeight: "600",
                      cursor: chatloading || isTyping ? "not-allowed" : "pointer", // <-- Change cursor
                      backgroundColor: chatloading || isTyping ? "#f3f4f6" : "#fff", // <-- Change color
                      opacity: chatloading || isTyping ? 0.6 : 1, // <-- Dim when loading
                      pointerEvents: chatloading || isTyping ? "none" : "auto", // <-- Disable click
                      ":hover": {
                        backgroundColor: chatloading ? "#f3f4f6" : "#f1f5f9",
                      },
                    }}
                    onClick={() => {
                      if (chatloading || isTyping) return;
                      setChatHistory((prev) => [
                        ...prev,
                        { sender: "user", message: text },
                      ]);
                      dispatch(
                        chatpracticeEvaluationThunk({
                          message: text,
                          student_test_id: props.student_test_id, // <-- Use prop here
                        })
                      );
                    }}
                  >
                    {text}
                  </Box>
                )
              )}
            </Box>
          </Box>

          {/* Mapped messages */}
          {chatHistory.map((entry, index) => (
            <Box
              key={index}
              sx={{
                mb: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: entry.sender === "user" ? "flex-end" : "flex-start",
              }}
            >
              {entry.sender === "bot" && (
                <>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                      justifyContent: "flex-start",
                    }}
                  >
                    <span style={{
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #2196f3 0%, #03a9f4 100%)",
                      width: 30,
                      height: 30,
                      justifyContent: "center",
                      padding: 4
                    }}>
                      <Avatar
                        src={AvatarImage}
                        className="chatbot-avatar"
                        alt="Profile"
                        sx={{ width: 22, height: 22 }}
                      />
                    </span>
                    Elliot
                  </Typography>
                </>
              )}
              <Box
                sx={{
                  backgroundColor: entry.sender === "user" ? "" : "#f9fafb",
                  color: entry.sender === "user" ? "#fff" : "#333",
                  borderRadius: "16px",
                  padding: 1.5,
                  maxWidth: "85%",
                  fontSize: "15px",
                }}
              >
                {entry.sender === "bot" ? (
                  <TypewriterMarkdown
                    content={entry.message}
                    isLast={index === chatHistory.length - 1}
                    onFinish={() => setIsTyping(false)}
                    onUpdate={() => {
                      if (divRef.current) {
                        divRef.current.scrollTop = divRef.current.scrollHeight;
                      }
                    }}
                    className="chat_bot_message"
                  />
                ) : (
                  <Markdown
                    children={entry.message}
                    remarkPlugins={[remarkMath]}
                    rehypePlugins={[rehypeKatex]}
                    className="user_message"
                  />
                )}
              </Box>
              {/* Feedback buttons for each bot response */}
              {entry.sender === "bot" && (
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  mt: 1,
                  mb: 1,
                }}>
                  <IconButtonField
                    size="small"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor: feedback === `helpful-${index}` ? "#4caf50" : "#fff",
                      color: feedback === `helpful-${index}` ? "#fff" : "#333",
                      borderRadius: "8px",
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      boxShadow: feedback === `helpful-${index}` ? "0 2px 8px rgba(76,175,80,0.12)" : "none",
                      transition: "background 0.2s, color 0.2s",
                      "&:hover": {
                        backgroundColor: "#4caf50",
                        color: "#fff",
                      },
                    }}
                    onClick={() => setFeedback(`helpful-${index}`)}
                  >
                    <ThumbUpOffAltIcon fontSize="small" />
                    Helpful
                    {feedback === `helpful-${index}` && <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />}
                  </IconButtonField>
                  <IconButtonField
                    size="small"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor: feedback === `not_helpful-${index}` ? "#f44336" : "#fff",
                      color: feedback === `not_helpful-${index}` ? "#fff" : "#333",
                      borderRadius: "8px",
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      boxShadow: feedback === `not_helpful-${index}` ? "0 2px 8px rgba(76,175,80,0.12)" : "none",
                      transition: "background 0.2s, color 0.2s",
                      "&:hover": {
                        backgroundColor: "#f44336",
                        color: "#fff",
                      },
                    }}
                    onClick={() => setFeedback(`not_helpful-${index}`)}
                  >
                    <ThumbDownOffAltIcon fontSize="small" />
                    Not helpful
                    {feedback === `not_helpful-${index}` && <CheckIcon fontSize="small" sx={{ ml: 0.5 }} />}
                  </IconButtonField>
                </Box>
              )}
            </Box>
          ))}

          {chatloading && (
            <Box sx={{ display: "flex", alignItems: "center", px: 3, py: 1 }}>
              <ChatDotsLoading />
              <span style={{ marginLeft: 12, color: "#6b7280", fontSize: "15px" }}>
                Analyzing and responding... give us a moment
              </span>
            </Box>
          )}
        </div>

        <Divider />

        <footer className="chatbot_footer" style={{ padding: "0 16px 16px" }}>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              ref={formRef}
              style={{
                display: "flex",
                width: "100%",
                maxWidth: "720px",
                marginTop: "25px",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <TextInputFieldC
                name="message"
                type="text"
                className="chat_input"
                size="small"
                fullWidth
                disabled={false}
                placeholder="Ask Elliot about any question or concept..."
                InputProps={{
                  sx: {
                    borderRadius: "12px",
                    border: "1px solid #d0d7de",
                    backgroundColor: "#fff",
                    fontSize: "15px",
                    px: 2,
                    py: 1,
                    boxShadow: "none",
                    outline: "none",
                    "&:hover, &:focus": {
                      border: "1px solid #d0d7de",
                      boxShadow: "none",
                    },
                  },
                }}
              />
              <IconButtonField
                type="submit"
                onClick={() => {
                  if (!chatloading && !isTyping) formRef.current?.requestSubmit();
                }}
                // disabled={chatloading || isTyping}
                sx={{
                  marginLeft: "8px",
                  backgroundColor: chatloading || isTyping ? "#bdbdbd" : "#009dff",
                  borderRadius: "8px",
                  width: "44px",
                  height: "44px",
                  minWidth: "unset",
                  boxShadow: "none",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  "&:hover": {
                    backgroundColor: chatloading || isTyping ? "#bdbdbd" : "#1976d2",
                  },
                  pointerEvents: chatloading || isTyping ? "none" : "auto",
                  cursor: chatloading || isTyping ? "not-allowed" : "pointer",
                }}
              >
                <img
                  src={sendImage}
                  alt="Send"
                  style={{ width: 17, height: 17 }}
                />
              </IconButtonField>
            </form>
          </FormProvider>
          <Typography sx={{ display: "flex", justifyContent: "center", color: "#6b7280", marginTop: "4px" }}>
            Elliot is here to help with your studies
          </Typography>
        </footer>
      </Drawer>
    </>
  );
};

export default ChatpracticeEvaluation;